import React, { forwardRef, useImperativeHandle, useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';

export interface OTPVerificationHandle {
  show: () => void;
  hide: () => void;
}

interface OTPVerificationModalProps {
  phoneNumber: string;
  onVerify: (otp: string) => void;
  onResend: () => void;
  remainingTime: number;
  isResendDisabled: boolean;
  error?: string | null;
  successMessage?: string | null;
  onError?: (error: string | null) => void;
  onSuccess?: (successMessage: string | null) => void;
  onClose?: () => void;
}

const OTPVerificationModal = forwardRef<OTPVerificationHandle, OTPVerificationModalProps>(
  ({phoneNumber, onVerify, onResend, remainingTime, isResendDisabled, error, successMessage, onError, onSuccess, onClose}, ref) => {
    const [visible, setVisible] = useState(false);
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Create stable functions that don't depend on state
    const showModal = useCallback(() => {
      setVisible(true);
    }, []);

    const hideModal = useCallback(() => {
      setVisible(false);
      setOtp(['', '', '', '', '', '']);
      onError?.(null);
      onSuccess?.(null);
    }, [onError, onSuccess]);

    // Use useImperativeHandle to expose the methods
    useImperativeHandle(ref, () => ({
      show: showModal,
      hide: hideModal,
    }), [showModal, hideModal]);

    const handleClose = useCallback(() => {
      hideModal();
      onClose?.();
    }, [hideModal, onClose]);

    // No-op; keep effect area clean of logs

    const handleOtpChange = (value: string, index: number) => {
      if (value.length > 1) return; // Only allow single digit

      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto focus next input
      if (value !== '' && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    };

    const handleVerify = () => {
      onVerify(otp.join(''));
    };

    const formatTime = (seconds: number) => {
      return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
    };

    // Close modal when clicking outside
    const handleOverlayClick = (e: React.MouseEvent) => {
      // Only close if clicking directly on the overlay, not on any child elements
      if (e.target === e.currentTarget) {
        handleClose();
      }
    };

    // Close modal on Escape key
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && visible) {
          handleClose();
        }
      };

      if (visible) {
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }, [visible, handleClose]);

    if (!visible) {
      return null;
    }
    return (
      <Overlay onClick={handleOverlayClick}>
        <Container onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={handleClose}>
            <CloseButtonText>✕</CloseButtonText>
          </CloseButton>

          <Title>Please verify your mobile number</Title>
          <Subtitle>
            Enter 6 digit OTP sent to {phoneNumber}
          </Subtitle>

          <OTPContainer>
            {otp.map((digit, index) => (
              <OTPInput
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                value={digit}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                hasError={!!error}
              />
            ))}
          </OTPContainer>

          {error && (
            <ErrorText>{error}</ErrorText>
          )}

          {successMessage && (
            <SuccessText>{successMessage}</SuccessText>
          )}

          <ResendContainerParent>
            <ResendText>Didn't receive OTP?</ResendText>
            <ResendButton 
              onClick={onResend}
              disabled={isResendDisabled}
            >
              <ResendText disabled={isResendDisabled}>
                {isResendDisabled ? (
                  `Resend OTP in ${formatTime(remainingTime)}`
                ) : (
                  <>
                    <ResendLink>resend OTP</ResendLink>
                  </>
                )}
              </ResendText>
            </ResendButton>
          </ResendContainerParent>

          <Footer>
            <VerifyButton
              onClick={handleVerify}
              disabled={otp.some((digit) => digit === '')}
            >
              Verify
            </VerifyButton>

            <TermsText>
              By proceeding, you are indicating that you have read agree to our{' '}
              <Link
                href="https://web.saloonify.com/termsandconditions"
                target="_blank"
                rel="noreferrer"
              >
                terms of use
              </Link>{' '}
              &{' '}
              <Link
                href="https://web.saloonify.com/privacypolicy"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </Link>
            </TermsText>
          </Footer>
        </Container>
      </Overlay>
    );
  }
);

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  z-index: 1000;
  
  /* Desktop styles */
  @media (min-width: 768px) {
    justify-content: center;
    align-items: center;
  }
`;

const Container = styled.div`
  background-color: white;
  border-radius: 16px 16px 0 0;
  padding: 56px 24px 32px;
  min-height: 50%;
  width: 100%;
  max-width: 100%;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  
  /* Desktop styles */
  @media (min-width: 768px) {
    max-width: 480px;
    border-radius: 16px;
    margin: 20px;
    min-height: auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  right: 24px;
  top: 24px;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  font-size: 20px;
  color: #6b7280;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const CloseButtonText = styled.span`
  font-size: 20px;
  color: #6b7280;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 24px;
  text-align: center;
`;

const OTPContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 32px;
`;

const OTPInput = styled.input<{ hasError: boolean }>`
  width: 48px;
  height: 56px;
  border: 2px solid ${props => props.hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 8px;
  text-align: center;
  font-size: 24px;
  color: #111827;
  font-weight: 600;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  margin-bottom: 16px;
`;

const SuccessText = styled.p`
  color: #10b981;
  font-size: 14px;
  text-align: center;
  margin-bottom: 16px;
`;

const ResendContainerParent = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  transition: color 0.2s;

  &:disabled {
    cursor: not-allowed;
    color: #9ca3af;
  }

  &:not(:disabled):hover {
    color: #3b82f6;
  }
`;

const ResendText = styled.span<{ disabled?: boolean }>`
  font-size: 14px;
  color: ${props => props.disabled ? '#9ca3af' : '#6b7280'};
`;

const ResendLink = styled.span`
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VerifyButton = styled.button`
  background-color: #B02E2F;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #B02E2F;
  }

  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const TermsText = styled.p`
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  line-height: 1.5;
`;

const Link = styled.a`
  color: #3b82f6;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #2563eb;
  }
`;

OTPVerificationModal.displayName = 'OTPVerificationModal';

export default OTPVerificationModal;
